import { Plus, Search, Users, X } from '@/components/ui/icons';
import { useMemo } from 'react';
import { Link, useMatch, useNavigate } from 'react-router';
import type { ConversationDTO } from '@clanfire/shared';
import { conversationApi } from '@/api/endpoints';
import { Avatar, CountBadge, GroupAvatar } from '@/components/ui/Avatar';
import { EmptyState } from '@/components/ui/Feedback';
import { Tooltip } from '@/components/ui/Popover';
import { conversationTitle, leaveGroup, recipientsOf } from '@/lib/conversations';
import { cn } from '@/lib/utils';
import { useConversations, useFriends } from '@/stores/social';
import { useUi } from '@/stores/ui';
import { usePresenceOf, useUsers } from '@/stores/users';

export function DmSidebar() {
  const conversations = useConversations((s) => s.conversations);
  const pending = useFriends((s) => s.pendingCount);
  const openModal = useUi((s) => s.openModal);
  const friendsActive = !!useMatch('/app/@me');

  const sorted = useMemo(
    () => Object.values(conversations).sort((a, b) => (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt)),
    [conversations],
  );

  return (
    <>
      <div className="flex h-[52px] shrink-0 items-center border-b border-line px-2.5">
        <button
          type="button"
          onClick={() => openModal({ type: 'quickSwitcher' })}
          aria-keyshortcuts="Control+K"
          className="flex h-8 w-full items-center gap-2 rounded-lg bg-sunken px-2.5 text-left text-sm text-fg-muted transition-colors hover:text-fg-soft"
        >
          <Search size={14} /> <span className="flex-1">Find or start a conversation</span>
          <kbd className="hidden rounded border border-line px-1 font-mono text-[10px] md:inline">Ctrl K</kbd>
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
        <Link
          to="/app/@me"
          aria-current={friendsActive ? 'page' : undefined}
          className={cn(
            'flex h-10 items-center gap-3 rounded-lg px-2.5 font-semibold transition-colors',
            friendsActive ? 'bg-active text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg-soft',
          )}
        >
          <Users size={20} />
          <span className="flex-1">Friends</span>
          <CountBadge count={pending} />
        </Link>

        <div className="mt-5 mb-1 flex items-center justify-between px-2">
          <h3 className="text-[11px] font-bold tracking-wider text-fg-muted uppercase">Direct messages</h3>
          <Tooltip label="New message or group">
            <button type="button" onClick={() => openModal({ type: 'newDm' })} aria-label="Start a direct message or group" className="rounded p-0.5 text-fg-muted hover:text-fg">
              <Plus size={16} />
            </button>
          </Tooltip>
        </div>
        {sorted.length === 0 ? (
          <EmptyState art="conversations" title="No conversations yet" body="Message a friend, or start a group with a few of them." className="py-6 [&_h3]:text-sm [&_svg]:h-20" />
        ) : (
          <ul className="flex flex-col gap-0.5">
            {sorted.map((c) => (
              <ConversationRow key={c.id} conversation={c} />
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function ConversationRow({ conversation }: { conversation: ConversationDTO }) {
  const active = !!useMatch(`/app/@me/${conversation.id}`);
  const navigate = useNavigate();
  const unread = conversation.unreadCount > 0 && !active;
  const isGroup = conversation.type === 'GROUP';
  const users = useUsers((s) => s.users);
  const recipients = recipientsOf(conversation, users);
  const title = conversationTitle(conversation, users);
  const user = isGroup ? null : (recipients[0] ?? null);
  const presence = usePresenceOf(user?.id);

  // The X closes a DM (it comes back with the next message) but leaves a group.
  const close = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isGroup) {
      await leaveGroup(conversation);
      return;
    }
    useConversations.getState().remove(conversation.id);
    if (active) navigate('/app/@me');
    await conversationApi.close(conversation.id).catch(() => undefined);
  };

  const subtitle = isGroup ? `${recipients.length + 1} members` : user?.customStatus;

  return (
    <li>
      <Link
        to={`/app/@me/${conversation.id}`}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'group flex h-11 items-center gap-3 rounded-lg px-2 transition-colors',
          active ? 'bg-active text-fg' : unread ? 'text-fg hover:bg-hover' : 'text-fg-muted hover:bg-hover hover:text-fg-soft',
        )}
      >
        {isGroup ? (
          <GroupAvatar users={recipients} size={32} ring={active ? 'ring-active' : 'ring-sidebar'} />
        ) : (
          <Avatar user={user} size={32} status={presence} ring={active ? 'ring-active' : 'ring-sidebar'} />
        )}
        <span className="min-w-0 flex-1">
          <span className={cn('block truncate text-[15px]', unread && 'font-bold')}>{title}</span>
          {subtitle && <span className="block truncate text-xs text-fg-muted">{subtitle}</span>}
        </span>
        {unread ? (
          <CountBadge count={conversation.unreadCount} />
        ) : (
          <button
            type="button"
            onClick={close}
            aria-label={isGroup ? `Leave ${title}` : `Close conversation with ${title}`}
            title={isGroup ? 'Leave group' : undefined}
            className="rounded p-0.5 text-fg-muted opacity-0 group-hover:opacity-100 hover:text-fg focus:opacity-100"
          >
            <X size={16} />
          </button>
        )}
      </Link>
    </li>
  );
}
